import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createBatch, fetchBatchReport, triggerBatchProcessing } from './utils';
import { fetchFolder } from '../redux/fetchFolder';
import { fetchDocuments } from '../redux/fetchDocuments';
import { useFetchNotifications } from '../redux/fetchNotifications';
import { BatchUploadReport } from './types';
import T from 'i18n';
import Logger from 'utils/logger';

export function useBatchProcess(caseId: string) {
  const dispatch = useDispatch();
  const { notifications } = useFetchNotifications();

  const [currentBatchId, setCurrentBatchId] = useState<number | null>(null);
  const [batchUploadReport, setBatchUploadReport] = useState<BatchUploadReport | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [batchSize, setBatchSize] = useState(0);

  const initialNotificationsLengthRef = useRef(notifications?.length ?? 0);
  const notificationsRef = useRef(notifications);
  const [resolveNotificationsPromise, setResolveNotificationsPromise] = useState<
    (() => void) | null
  >(null);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    if (
      notifications?.length > initialNotificationsLengthRef.current &&
      resolveNotificationsPromise
    ) {
      resolveNotificationsPromise();
      setResolveNotificationsPromise(null);
    }
  }, [notifications, resolveNotificationsPromise]);

  const waitForNotifications = useCallback(() => {
    initialNotificationsLengthRef.current = notificationsRef.current?.length ?? 0;
    return new Promise<void>((resolve) => {
      const timeoutId = setTimeout(() => {
        setResolveNotificationsPromise(null);
        resolve();
      }, 15000);
      setResolveNotificationsPromise(() => () => {
        clearTimeout(timeoutId);
        resolve();
      });
    });
  }, []);

  const refreshData = useCallback(
    (folderId: string) => {
      dispatch(fetchFolder({ folderId, noPromise: false }));
      dispatch(fetchDocuments({} as any));
    },
    [dispatch],
  );

  const proceedToCreateBatch = useCallback(
    async (folderId: string, data: any[], rootFolderLink: string, templateId?: string) => {
      try {
        setBatchSize(data.length);
        setUploadInProgress(true);

        const batch: any = await createBatch(caseId, folderId, data, templateId, rootFolderLink);
        setCurrentBatchId(batch.id);

        await triggerBatchProcessing(batch.id, caseId);
        await waitForNotifications();
        refreshData(folderId);

        setUploadComplete(true);
        setUploadInProgress(false);

        await fetchBatchReport(caseId, batch.id, setBatchUploadReport);
      } catch (err) {
        Logger.ERROR(T.translate('case.batchUpload.apiError'), err);
        setUploadInProgress(false);
        throw err;
      }
    },
    [caseId, waitForNotifications, refreshData],
  );

  const markComplete = useCallback(() => {
    setUploadComplete(true);
    setUploadInProgress(false);
  }, []);

  const reset = useCallback(() => {
    setCurrentBatchId(null);
    setBatchUploadReport(null);
    setUploadComplete(false);
    setUploadInProgress(false);
    setBatchSize(0);
  }, []);

  return {
    currentBatchId,
    batchUploadReport,
    uploadComplete,
    uploadInProgress,
    batchSize,
    proceedToCreateBatch,
    markComplete,
    reset,
  };
}
